import { useEffect, useMemo, useRef, useState } from "react";
import { useAppDispatch, useAppSelector } from "@store/index";
import {
  lineChartConfigSelector,
  lineChartConfigDispatcher,
} from "@store/slices/line-chart-config-slice";
import { useLazyGetDatasetQuestionsListQuery } from "@store/api/dataset-api";
import type { DatasetMetric } from "@models/Metric";
import type { QuestionDetail } from "@models/Dataset";
import {
  getMetricResultCandidates,
  metricBaseToVisualizationBase,
} from "@utils/dataset-metric-representation-utils";
import {
  lineChartMetricNeedsQuestionDetailFetch,
  buildLineChartFocusParamsFromMetric,
} from "@components/charts/line-chart-metric-utils";
import type { useLineChartConfigDrawer } from "@hooks/use-line-chart-config-drawer";

type FetchLineChartData = ReturnType<typeof useLineChartConfigDrawer>["fetchLineChartData"];

/**
 * Line Chart's counterpart to Bar Chart's useMetricSourceRevalidation
 * (bar-chart-config/hooks/useMetricSourceRevalidation.ts) — same resolution
 * steps and the same reopen-time-auto-apply-without-fetching design (see
 * that file's doc comment for the full same-tick-dispatch-race reasoning
 * this mirrors). Breakout fields are intentionally left untouched here —
 * Breakout is a local reporting setting the researcher controls directly
 * (PRD-67), not part of the metric's own definition, so a metric refresh
 * never needs to override it; handleRefresh's fetch just carries whatever
 * breakout state is already in Redux.
 */
export function useLineMetricSourceRevalidation(
  datasetId: string | null,
  metrics: DatasetMetric[] | undefined,
  datasetLoaded: boolean,
  fetchLineChartData: FetchLineChartData,
) {
  const dispatch = useAppDispatch();
  const { currentConfig, questionId } = useAppSelector(lineChartConfigSelector);
  const {
    updateFocusParams: updateLineFocusParams,
    updateFocusRollup: updateLineFocusRollup,
    updateCategoryRollups: updateLineCategoryRollups,
    updateVisualizationBase,
    updateDatasetMetricSource,
    updateTitle: updateLineChartTitle,
    updateResolvedQuestionId,
  } = lineChartConfigDispatcher(dispatch);

  const isMetricSourced = currentConfig.chartSourceType === "metric";
  const datasetMetricId = currentConfig.datasetMetricId ?? null;

  const liveMetric = useMemo(
    () => (datasetMetricId ? (metrics?.find((m) => m.id === datasetMetricId) ?? null) : null),
    [metrics, datasetMetricId],
  );
  const isRemoved = isMetricSourced && !!datasetMetricId && datasetLoaded && !liveMetric;
  const isUnavailableMean = false; // Line Chart supports Mean metrics — never unavailable for this reason.

  const [questionDetail, setQuestionDetail] = useState<QuestionDetail | null>(null);
  const [fetchError, setFetchError] = useState(false);
  const [fetchQuestionDetails, { isFetching: isFetchingDetails }] =
    useLazyGetDatasetQuestionsListQuery();

  const needsFetch =
    !!liveMetric && lineChartMetricNeedsQuestionDetailFetch(liveMetric);

  const runFetch = () => {
    if (!liveMetric?.source || !datasetId) return;
    setFetchError(false);
    fetchQuestionDetails({
      id: datasetId,
      page: 1,
      page_size: 10,
      search_query: liveMetric.source.question.questionLabel,
      dataset_element_id: liveMetric.source.question.questionId,
      expanded: true,
    })
      .unwrap()
      .then((result) => {
        const detail = result.questions.find(
          (q) => q.datasetElementId === liveMetric.source!.question.questionId,
        );
        setQuestionDetail(detail ?? null);
      })
      .catch(() => setFetchError(true));
  };

  useEffect(() => {
    if (!liveMetric?.source || !datasetId || !needsFetch) {
      setQuestionDetail(null);
      setFetchError(false);
      return;
    }
    runFetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liveMetric?.id, datasetId, needsFetch]);

  const freshFocusParams = useMemo(() => {
    if (!liveMetric) return null;
    if (needsFetch && !questionDetail) return null;
    return buildLineChartFocusParamsFromMetric(liveMetric, questionDetail);
  }, [liveMetric, needsFetch, questionDetail]);

  const definitionChanged = useMemo(() => {
    if (!liveMetric || isRemoved || !freshFocusParams) return false;
    const oldSignature = JSON.stringify({
      base: currentConfig.datasetMetricBase,
      population: currentConfig.datasetMetricPopulation,
      focusMode: currentConfig.focusMode ?? null,
      focusEntity: currentConfig.focusEntity ?? null,
      focusRollup: currentConfig.focusRollup ?? null,
      categoryRollups: currentConfig.categoryRollupsByScope?.default ?? null,
    });
    const newSignature = JSON.stringify({
      base: liveMetric.base,
      population: liveMetric.population,
      focusMode: freshFocusParams.focusMode ?? null,
      focusEntity: freshFocusParams.focusEntity ?? null,
      focusRollup: freshFocusParams.focusRollup ?? null,
      categoryRollups: freshFocusParams.categoryRollups ?? null,
    });
    return oldSignature !== newSignature;
  }, [
    liveMetric,
    isRemoved,
    freshFocusParams,
    currentConfig.datasetMetricBase,
    currentConfig.datasetMetricPopulation,
    currentConfig.focusMode,
    currentConfig.focusEntity,
    currentConfig.focusRollup,
    currentConfig.categoryRollupsByScope,
  ]);

  const representedResults = useMemo(
    () => (liveMetric ? getMetricResultCandidates(liveMetric, questionDetail) : []),
    [liveMetric, questionDetail],
  );

  // A shared metric edit can remove/rename the represented result the
  // researcher chose as Breakout's Source Line. Detected by label rather
  // than a real variable/value id (which this hook doesn't have — that's
  // LineChartBreakoutSection's own fetch) — sourceLineLabel was captured at
  // selection time from the exact same display fields (rowtitle/title/label)
  // that representedResults' matchLabels are matched against, so this is a
  // faithful "is the old selection still one of the metric's current
  // results" check without duplicating that fetch. Per PRD-67: identify the
  // repair needed rather than silently dropping the saved selection.
  const breakoutSourceInvalidated = useMemo(() => {
    if (!liveMetric || isRemoved || !currentConfig.breakout?.enabled) return false;
    const label = currentConfig.breakout.sourceLineLabel;
    if (!label) return false;
    const lower = label.toLowerCase();
    return !representedResults.some((r) =>
      r.matchLabels.some((l) => l.toLowerCase() === lower),
    );
  }, [
    liveMetric,
    isRemoved,
    currentConfig.breakout?.enabled,
    currentConfig.breakout?.sourceLineLabel,
    representedResults,
  ]);

  const applyLiveDefinition = () => {
    if (!liveMetric || !freshFocusParams) return null;
    const wasDefaultTitle = currentConfig.title === currentConfig.datasetMetricName;

    // The metric's source question is itself editable in Dataset
    // Configuration — keep the root questionId (used by every in-editor
    // fetch, and by Breakout's mainQuestionId) pointed at the metric's
    // CURRENT source question rather than the save-time/creation-time one.
    // DashboardFilters.tsx already does the read-only-view equivalent of this.
    const liveQuestionId = liveMetric.source?.question.questionId;
    if (liveQuestionId && liveQuestionId !== questionId) {
      updateResolvedQuestionId(liveQuestionId);
    }

    updateLineFocusRollup(
      freshFocusParams.focusRollup
        ? { rollup: freshFocusParams.focusRollup, name: freshFocusParams.focusRollupName }
        : null,
    );
    updateLineFocusParams(
      freshFocusParams.focusMode
        ? {
            focusMode: freshFocusParams.focusMode,
            focusEntity: freshFocusParams.focusEntity ?? "",
            focusEntityDisplayLabel: freshFocusParams.focusEntityDisplayLabel,
          }
        : null,
    );
    updateLineCategoryRollups(freshFocusParams.categoryRollups ?? null);
    const visualizationBase = metricBaseToVisualizationBase(liveMetric.base);
    updateVisualizationBase(visualizationBase);
    updateDatasetMetricSource({
      datasetMetricId: liveMetric.id,
      datasetMetricName: liveMetric.name,
      base: liveMetric.base,
      population: liveMetric.population,
      representedResults,
    });
    if (wasDefaultTitle) updateLineChartTitle(liveMetric.name);

    return visualizationBase;
  };

  const handleRefresh = () => {
    if (!liveMetric) return;
    const visualizationBase = applyLiveDefinition();
    if (!visualizationBase || !freshFocusParams) return;

    void fetchLineChartData(
      true,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      freshFocusParams.focusMode
        ? { focusMode: freshFocusParams.focusMode, focusEntity: freshFocusParams.focusEntity ?? "" }
        : undefined,
      freshFocusParams.focusRollup ?? null,
      freshFocusParams.categoryRollups ?? null,
      visualizationBase,
      liveMetric.population.mode === "total_survey_population",
      // The updateResolvedQuestionId dispatch above is async relative to
      // this closure — pass the live value directly so this fetch doesn't
      // race it and read the stale root questionId.
      liveMetric.source?.question.questionId,
    );
  };

  const hasAutoAppliedOnReopenRef = useRef(false);
  useEffect(() => {
    if (hasAutoAppliedOnReopenRef.current || !liveMetric || !freshFocusParams) return;
    hasAutoAppliedOnReopenRef.current = true;
    applyLiveDefinition();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liveMetric, freshFocusParams]);

  const isResolving = needsFetch && !questionDetail && !fetchError;
  const isSettled =
    !isMetricSourced || isRemoved || (!!liveMetric && !isResolving && !definitionChanged);

  return {
    isMetricSourced,
    liveMetric,
    isRemoved,
    isUnavailableMean,
    definitionChanged,
    breakoutSourceInvalidated,
    isResolving,
    isSettled,
    fetchError,
    isFetchingDetails,
    handleRefresh,
    retryFetch: runFetch,
    representedResults,
    questionDetail,
  };
}
